import { VisitsHeatmapData } from "@/services/api/trackReports";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect } from "react";
import MapTileLayers from "@/components/Map/MapTileLayers";
import { Circle, MapContainer, Tooltip, useMap } from "react-leaflet";

interface VisitsReportHeatmapProps {
  data: VisitsHeatmapData;
}

const MapController = ({ data }: { data: VisitsHeatmapData }) => {
  const map = useMap();

  useEffect(() => {
    if (data.reference_points.length > 0) {
      const bounds = L.latLngBounds(
        data.reference_points.map((point) => [point.latitude, point.longitude]),
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [data, map]);

  return null;
};

const HeatmapLegend = ({ data }: { data: VisitsHeatmapData }) => {
  return (
    <div className="absolute bottom-6 right-6 z-1000 bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 min-w-[180px]">
      <h4 className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-2 uppercase tracking-wider">
        Frequência de Visitas
      </h4>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 mb-1">
          <span>{data.min_visits} visitas (mín)</span>
          <span>{data.max_visits} visitas (máx)</span>
        </div>
        <div className="flex h-2.5 w-full rounded-full overflow-hidden">
          {data.color_scale.map((color, index) => (
            <div
              key={index}
              className="h-full flex-1"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">
            Menos
          </span>
          <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">
            Mais
          </span>
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-700">
        <p className="text-[10px] text-slate-500 dark:text-slate-400">
          Total de pontos:{" "}
          <span className="font-bold">{data.total_points}</span>
        </p>
      </div>
    </div>
  );
};

const VisitsReportHeatmap = ({ data }: VisitsReportHeatmapProps) => {
  const zoom = 4;

  return (
    <div className="w-full h-[600px] relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-xs">
      <MapContainer
        center={[-11.59293, -49.597924]}
        zoom={zoom}
        className="w-full h-full z-0"
        doubleClickZoom={false}
      >
        <MapTileLayers position="topright" />

        <MapController data={data} />

        {data.reference_points.map((point) => (
          <Circle
            key={point.id}
            center={[point.latitude, point.longitude]}
            radius={point.radius || 100}
            pathOptions={{
              color: point.color,
              fillColor: point.color,
              fillOpacity: 0.6,
              weight: 2,
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={1}>
              <div className="p-1">
                <p className="font-bold text-sm mb-0.5 text-muted-foreground">
                  {point.name}
                </p>
                <p className="text-xs text-slate-600 m-0">
                  {point.visits_count} visitas no período
                </p>
              </div>
            </Tooltip>
          </Circle>
        ))}
      </MapContainer>
      <HeatmapLegend data={data} />
    </div>
  );
};

export default React.memo(VisitsReportHeatmap);
